import React, { useEffect, useState, useRef } from 'react';
import { LoadingSkeleton } from '@/components/ui/loading-skeleton';

interface LazySectionProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  rootMargin?: string;
  threshold?: number;
  minHeight?: string;
  /** Explicit height to prevent CLS - skeleton will match this */
  expectedHeight?: string;
}

/**
 * LazySection - Defers rendering of below-the-fold sections until they're near the viewport.
 * Uses Intersection Observer to trigger loading only when needed.
 */
export const LazySection: React.FC<LazySectionProps> = ({
  children,
  fallback,
  rootMargin = '200px',
  threshold = 0,
  minHeight = '200px',
  expectedHeight
}) => {
  // Use expectedHeight if provided for better CLS prevention
  const reservedHeight = expectedHeight || minHeight;
  const [shouldRender, setShouldRender] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = sectionRef.current;
    if (!element) return;

    // If IntersectionObserver is not supported, render immediately
    if (!('IntersectionObserver' in window)) {
      setShouldRender(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldRender(true);
          observer.disconnect();
        }
      },
      { rootMargin, threshold }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [rootMargin, threshold]);

  const defaultFallback = (
    <div 
      className="w-full py-16" 
      style={{ 
        minHeight: reservedHeight,
        // Prevent layout shift by matching expected content dimensions
        contain: 'layout paint',
      }}
    >
      <div className="container mx-auto px-4">
        <LoadingSkeleton variant="rectangular" className="w-full h-32 mb-4" />
        <LoadingSkeleton variant="text" lines={3} className="max-w-2xl mx-auto" />
      </div>
    </div>
  );

  return (
    <div 
      ref={sectionRef} 
      style={{ 
        minHeight: shouldRender ? 'auto' : reservedHeight,
        // Reserve space even before render to prevent CLS
        containIntrinsicSize: `0 ${reservedHeight}`,
        contentVisibility: shouldRender ? 'visible' : 'auto',
      }}
    >
      {shouldRender ? children : (fallback || defaultFallback)}
    </div>
  );
};

export default LazySection;
